/**
 * KBS cookie consent
 * ------------------
 * orestbida CookieConsent v3 (https://cookieconsent.orestbida.com/)
 * Self-hosted, bundled via laravel-mix -> /assets/js/consent.js
 * Loaded SYNCHRONOUSLY in <head> (templates/base/dochead*.twig) BEFORE any tracker
 * so we can set Google Consent Mode to "denied" before gtag runs, and hold
 * gated scripts until the visitor opts in.
 *
 * Because the site is served statically by Blitz, all gating is client-side.
 *
 * MULTISITE: the banner adapts per site using flags emitted by base/_consentTags.twig:
 *   window.kbsConsent = { ads: <bool>, hubspot: <bool> }
 * read live from SEOmatic's configured IDs for the current site. So Default/Canada
 * (Google Ads + HubSpot) show the Marketing category + HubSpot rows, while IFS
 * (GA4 only) shows neither. GA4 IDs differ per site, so the GA4 disclosure is generic.
 *
 * Category map:
 *   analytics  -> GA4 (per-site property, via SEOmatic) + Vimeo (dnt-gated) + HubSpot (if present)
 *   marketing  -> Google Ads (if present) — rides in SEOmatic's gtag; gated via Consent Mode
 *   necessary  -> always on (Freeform CSRF, session, the consent cookie itself)
 *
 * SEOmatic-injected scripts (gtag, hubSpot) are tagged type="text/plain" data-category
 * at render via base/_consentTags.twig using seomatic.script.get(handle).tagAttrs(...).
 *
 * NOT gated, by decision:
 *   HubSpot forms (the embed itself) -> functional lead capture; left on
 *   Careers GA   -> external careers.kbs-services.com Paradox portal (out of scope)
 */

import * as CookieConsent from 'vanilla-cookieconsent';

// Expose globally so a "Cookie Settings" link anywhere on the page can reopen
// the preferences panel: onclick="CookieConsent.showPreferences()"
window.CookieConsent = CookieConsent;

/* -------------------------------------------------------------------------
 * Google Consent Mode v2 — default everything to denied BEFORE gtag loads.
 * Runs first because consent.js is the first script in <head>.
 * ---------------------------------------------------------------------- */
window.dataLayer = window.dataLayer || [];
function gtag() { window.dataLayer.push(arguments); }
window.gtag = window.gtag || gtag;

gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
});

function syncGoogleConsent() {
    const analytics = CookieConsent.acceptedCategory('analytics') ? 'granted' : 'denied';
    const marketing = CookieConsent.acceptedCategory('marketing') ? 'granted' : 'denied';
    gtag('consent', 'update', {
        analytics_storage: analytics,
        // Google Ads rides inside SEOmatic's gtag (gated under "analytics").
        // Consent Mode separates the ad portion: it only stores/uses ad data
        // if the visitor also accepts "marketing".
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
    });
}

// Vimeo inline embeds render with dnt=1 (private) by default in the cached HTML.
// Flip to dnt=0 (tracked) for visitors who accept Analytics, and back on revoke.
// (Lightbox/Plyr videos read consent separately, in modals.js.)
function syncVimeo() {
    const allow = CookieConsent.acceptedCategory('analytics');
    const from = allow ? 'dnt=1' : 'dnt=0';
    const to = allow ? 'dnt=0' : 'dnt=1';
    document.querySelectorAll('iframe[src*="player.vimeo.com"]').forEach((f) => {
        if (f.src.includes(from)) {
            f.src = f.src.replace(from, to); // reassigning src reloads the player
        }
    });
}

function applyConsent() {
    syncGoogleConsent();
    syncVimeo();
}

/* -------------------------------------------------------------------------
 * Build the config from the per-site flags. Defaults to showing everything if
 * the flag is missing, so we never under-disclose.
 * ---------------------------------------------------------------------- */
const flags = window.kbsConsent || { ads: true, hubspot: true };

// ----- Analytics: cookie disclosure (GA4 generic; HubSpot only where present) -----
const analyticsCookieRows = [
    { name: '_ga', service: 'Google Analytics', desc: 'Distinguishes unique visitors', exp: '2 years' },
    { name: '_ga_*', service: 'Google Analytics', desc: 'GA4 session state (one per property)', exp: '2 years' },
    { name: '_gid', service: 'Google Analytics', desc: 'Distinguishes unique visitors', exp: '24 hours' },
    { name: 'vuid', service: 'Vimeo', desc: 'Counts video views (only on play, with consent)', exp: '2 years' },
];
const analyticsAutoClear = [
    { name: /^_ga/ },
    { name: '_gid' },
];
if (flags.hubspot) {
    analyticsCookieRows.push(
        { name: '__hstc', service: 'HubSpot', desc: 'Visitor analytics across sessions', exp: '6 months' },
        { name: '__hssc', service: 'HubSpot', desc: 'Tracks the current session', exp: '30 minutes' },
        { name: 'hubspotutk', service: 'HubSpot', desc: 'Visitor ID; links form submissions to your contact record', exp: '6 months' },
    );
    analyticsAutoClear.push({ name: 'hubspotutk' }, { name: /^__hs/ }, { name: 'messagesUtk' });
}

// ----- Categories (marketing where Google Ads and/or LinkedIn is present) -----
const categories = {
    necessary: { enabled: true, readOnly: true },
    analytics: { autoClear: { cookies: analyticsAutoClear } },
};
if (flags.ads || flags.linkedin) {
    // Note: LinkedIn cookies are third-party (.linkedin.com) so autoClear can't reach
    // them — they're prevented at the source via the gated script. Only the first-party
    // Google Ads cookie is auto-cleared here.
    categories.marketing = { autoClear: { cookies: flags.ads ? [{ name: /^_gcl/ }] : [] } };
}

// ----- Preferences-modal sections -----
const sections = [
    {
        title: 'Strictly necessary',
        description: 'Required for the site to function. Always on.',
        linkedCategory: 'necessary',
        cookieTable: {
            headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
            body: [
                { name: 'CraftSessionId', service: 'KBS', desc: 'Maintains your session as you browse', exp: 'Session' },
                { name: 'CRAFT_CSRF_TOKEN', service: 'KBS', desc: 'Protects forms against cross-site request forgery', exp: 'Session' },
                { name: 'cc_cookie', service: 'KBS', desc: 'Stores your cookie consent choices', exp: '6 months' },
                { name: 'freeform_*', service: 'KBS', desc: 'Spam protection and submission handling on forms', exp: 'Session' },
                { name: '__cf_bm', service: 'Cloudflare (Vimeo, HubSpot)', desc: 'Bot protection for embedded video and forms', exp: '30 minutes' },
                { name: '_cfuvid', service: 'Cloudflare (Vimeo, HubSpot)', desc: 'Rate-limiting for embedded video and forms', exp: 'Session' },
            ],
        },
    },
    {
        title: 'Analytics',
        description: flags.hubspot
            ? 'Google Analytics, Vimeo video stats, and HubSpot form analytics — help us understand site usage, which videos people watch, and how forms perform.'
            : 'Google Analytics and Vimeo video stats — help us understand site usage and which videos people watch.',
        linkedCategory: 'analytics',
        cookieTable: {
            headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
            body: analyticsCookieRows,
        },
    },
];
if (flags.ads || flags.linkedin) {
    const marketingRows = [];
    if (flags.ads) {
        marketingRows.push({ name: '_gcl_au', service: 'Google Ads', desc: 'Measures ad clicks and conversions', exp: '3 months' });
    }
    if (flags.linkedin) {
        marketingRows.push(
            { name: 'bcookie', service: 'LinkedIn', desc: 'Browser ID for ads and conversion tracking', exp: '1 year' },
            { name: 'lidc', service: 'LinkedIn', desc: 'Routing for LinkedIn services', exp: '1 day' },
            { name: 'UserMatchHistory', service: 'LinkedIn', desc: 'Ad targeting and identifier sync', exp: '30 days' },
            { name: 'AnalyticsSyncHistory', service: 'LinkedIn', desc: 'Sync of advertising identifiers', exp: '30 days' },
            { name: 'li_sugr', service: 'LinkedIn', desc: 'Browser identifier for analytics and ads', exp: '90 days' },
        );
    }
    const marketingServices = [flags.ads ? 'Google Ads' : null, flags.linkedin ? 'LinkedIn' : null].filter(Boolean).join(' and ');
    sections.push({
        title: 'Marketing',
        description: `${marketingServices} — used to measure ad performance and reach you with relevant advertising.`,
        linkedCategory: 'marketing',
        cookieTable: {
            headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
            body: marketingRows,
        },
    });
}
sections.push({
    title: 'More information',
    description: 'See our <a href="/privacy-policy">Privacy Policy</a> for details.',
});

const consentDescription = (flags.ads || flags.linkedin)
    ? 'We use cookies to run the site, understand how it’s used, and support our advertising. ' +
      'You can accept all, reject non-essential, or choose your preferences.'
    : 'We use cookies to run the site and understand how it’s used. ' +
      'You can accept all, reject non-essential, or choose your preferences.';

const ccConfig = {
    guiOptions: {
        consentModal: { layout: 'box wide', position: 'bottom left' },
        preferencesModal: { layout: 'box' },
    },

    categories: categories,

    onFirstConsent: applyConsent,
    onConsent: applyConsent,
    // When the visitor CHANGES their choices later (via the "Cookie Settings" link),
    // reload so every tracker re-initialises cleanly with the new state — this covers
    // the lightbox videos and any blocked scripts, not just the live-synced ones.
    // (onChange does not fire on the first banner choice, so the initial accept/reject
    // applies live without a reload.)
    onChange: () => window.location.reload(),

    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    title: 'We use cookies',
                    description: consentDescription,
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    showPreferencesBtn: 'Manage preferences',
                },
                preferencesModal: {
                    title: 'Cookie preferences',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    savePreferencesBtn: 'Save preferences',
                    closeIconLabel: 'Close',
                    sections: sections,
                },
            },
        },
    },
};

// run() appends the banner to document.body, which does not exist yet when this
// file executes synchronously in <head>. Defer the UI until the DOM is ready.
// (The Consent Mode default above already ran in <head>, before any tracker.)
function startConsent() {
    CookieConsent.run(ccConfig);
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startConsent);
} else {
    startConsent();
}
